import { useState } from 'react'
import { MotionLayout } from '@/lib/motion/lazy-motion'
import { motion } from 'motion/react'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import AppStack from '@/components/content/app/AppStack'
import Popover from '@/components/ui/Popover'
import AppIcon from '@/components/content/app/AppIcon'
import DealClaim from './DealClaim'
import Timestamp from '@/components/ui/Timestamp'
import { type DealOverviewProps } from './DealSummary'

import { Icon } from '@/icons/Icon'
import { cn, hexToRgba, getBrandColors } from '@/lib/utils'
import { formatDeal, formatDealButton, formatEligibility } from '@/components/content/deals/helpers'
import { useDealContext } from '@/context/DealContext'
import { usePageContext } from '@/components/content/common/PageContext'
import { checkAppBranding } from '@/lib/formatting'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { MenuItem } from '@/helpers/menus'
import type { App } from '@/payload-types'
import { Timestamps } from '@/types/app'

type DealHeroProps = DealOverviewProps & {
  app: App
  link?: string
  passReferrerUrl?: boolean
  sponsored?: boolean
  multiple?: boolean
  menu: MenuItem[]
  subMenu?: MenuItem[]
  timestamps: Timestamps
}

export default function DealHero({
  app,
  deal,
  deals,
  link,
  passReferrerUrl,
  sponsored,
  multiple,
  subMenu,
  timestamps,
}: DealHeroProps) {
  const { claimed } = useDealContext()
  const formattedEligibility = formatEligibility(deal.eligibility)

  const [subMenuVisible, setSubMenuVisible] = useState(false)
  const { md, lg } = useBreakpoint()

  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app)

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
        #000`

  return (
    <Hero
      gradient={gradient}
      ftc={true}
      animation={{ scroll: false, scale: true }}
      className="pt-[128px] md:pt-0"
    >
      {({ animations }) => (
        <Container
          className="relative flex h-full items-center justify-center md:pt-20"
          height="fill"
        >
          <MotionLayout>
            <div className="z-20 flex w-full -translate-y-10 flex-col items-center justify-center gap-7 md:translate-y-0">
              <motion.div className="relative" style={animations}>
                {(appIcon || appGlyph) && (
                  <AppStack
                    icon={appIcon}
                    glyph={appGlyph}
                    name={app.name}
                    multiple={multiple}
                    background={backgroundColor}
                    highlight={claimed}
                    invert={true}
                    size={md ? 'lg' : 'sm'}
                    loading="eager"
                  />
                )}

                {subMenu && subMenu.length > 0 && (
                  <div className="absolute right-3.5 top-3.5 z-30">
                    <Popover
                      menu={subMenu}
                      position="down"
                      align={lg ? 'left' : 'right'}
                      visible={subMenuVisible}
                      setVisible={setSubMenuVisible}
                    >
                      <div className="h-6 w-6 cursor-pointer rounded-full border-white/20 bg-gradient-to-b from-white to-white/80 text-black transition-colors hover:bg-white">
                        <span
                          className={cn(
                            'flex h-full w-full items-center justify-center transition-transform duration-300 ease-in-out',
                            subMenuVisible ? 'rotate-90' : '-rotate-90',
                          )}
                        >
                          <Icon name="chevron-left" size={12} />
                        </span>
                      </div>
                    </Popover>
                  </div>
                )}
              </motion.div>

              <motion.div style={animations} className="flex flex-col items-center gap-6">
                <p
                  aria-hidden="true"
                  className="text-center font-heading text-36 font-bold md:text-64"
                >
                  {app.name} Deal{multiple && 's'}
                </p>

                {timestamps && (
                  <Timestamp
                    modified={timestamps.modified}
                    published={timestamps.published}
                    lead="Updated"
                    className="sm:hidden"
                  />
                )}

                <DealToggle deals={deals} deal={deal} />

                {link && (
                  <div className="flex flex-col items-center gap-3">
                    <DealClaim
                      key={deal.id}
                      link={link}
                      passReferrerUrl={passReferrerUrl}
                      sponsored={sponsored}
                      label={formatDealButton(deal)}
                      background={backgroundColor}
                      emailRequired={deal.emailIsRequired}
                      desktopIsRequired={deal.desktopIsRequired}
                      promoCode={deal.promoCode}
                      eligibility={`For ${formattedEligibility.label}${formattedEligibility.only ? ' only' : ''}`}
                      allowsLogin={!formattedEligibility.only}
                      logo={
                        appIcon || appGlyph ? (
                          <AppIcon
                            icon={appIcon}
                            glyph={appGlyph}
                            name={app.name}
                            background={backgroundColor}
                            size={32}
                            loading="eager"
                          />
                        ) : null
                      }
                    />
                  </div>
                )}
              </motion.div>
            </div>
          </MotionLayout>
        </Container>
      )}
    </Hero>
  )
}

export function DealToggle({ deals, deal }: DealOverviewProps) {
  const { setDealFocus } = usePageContext()

  return (
    <div
      className={cn(
        'mb-2 flex h-auto w-fit max-w-full flex-row flex-wrap items-center justify-center gap-1 rounded-[26px] border border-white/20 p-1.5 text-16',
      )}
    >
      {deals &&
        deals.length > 0 &&
        deals.map((d) => {
          const formattedDeal = formatDeal(d)

          return (
            <div
              onClick={() => setDealFocus(d)}
              className="relative flex h-10 grow cursor-pointer items-center justify-center px-3"
              key={d.id}
            >
              <span className="relative z-20 whitespace-nowrap text-15 font-medium text-white">
                {formattedDeal.shortname}
              </span>

              {d.id === deal.id && (
                <motion.div
                  layoutId="highlight"
                  className="absolute left-0 top-0 z-10 h-full w-full rounded-full bg-white/10"
                />
              )}
            </div>
          )
        })}
    </div>
  )
}
